import { ReactNode, useEffect } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { SERVER_TRPC_BASE_PATH } from "@simplyk/common";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";

import { edgeLocalisationUrl, trpcUrl } from "@/common/constants/env";
import { isDashboardPathname } from "@/common/helpers/amplitudeAutocapture";
import { readDeviceCapabilities } from "@/common/helpers/deviceCapabilities";

interface DatadogProviderProps {
  children: ReactNode;
}

const acceptsTracingHeaders = (url: string): boolean =>
  url.startsWith(process.env.NEXT_PUBLIC_API_URL || "") && !url.startsWith(edgeLocalisationUrl);

export const DatadogProvider = ({ children }: DatadogProviderProps) => {
  const pathname = usePathname();
  const { asPath, pathname: routeTemplate, locale } = useRouter();
  const path = asPath.split(/[?#]/)[0];

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN;
    const serviceName = process.env.NEXT_PUBLIC_DD_SERVICE_NAME;
    const applicationId = process.env.NEXT_PUBLIC_DD_APPLICATION_ID;

    if (!clientToken || !serviceName) {
      // eslint-disable-next-line no-console
      console.warn("Datadog client token or service name not provided. Logging disabled.");
      return;
    }

    // Initialize Datadog logs
    datadogLogs.init({
      clientToken,
      site: "datadoghq.com",
      service: serviceName,
      env: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV,
      version: process.env.NEXT_PUBLIC_APP_VERSION,
      forwardErrorsToLogs: false,
      sessionSampleRate: 100,
    });

    // Initialize Datadog RUM
    if (applicationId) {
      datadogRum.init({
        applicationId,
        clientToken,
        site: "datadoghq.com",
        service: serviceName,
        env: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV,
        version: process.env.NEXT_PUBLIC_APP_VERSION,
        sessionSampleRate: Number(process.env.NEXT_PUBLIC_DD_SESSION_SAMPLE_RATE) || 5,
        sessionReplaySampleRate: Number(process.env.NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE) || 5,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask",
        startSessionReplayRecordingManually: true,
        // Enable distributed tracing to link frontend RUM views to backend traces
        allowedTracingUrls: [
          { match: acceptsTracingHeaders, propagatorTypes: ["tracecontext", "datadog"] },
          { match: `${trpcUrl}${SERVER_TRPC_BASE_PATH}`, propagatorTypes: ["tracecontext", "datadog"] },
        ],
      });

      const { deviceMemoryGb, hardwareConcurrency, saveData } = readDeviceCapabilities();
      datadogRum.setGlobalContextProperty("device_capabilities", {
        device_memory_gb: deviceMemoryGb,
        hardware_concurrency: hardwareConcurrency,
        save_data: saveData,
      });
    }
  }, []);

  // Donors type personal data on public forms, so session replay only records the organization dashboard.
  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_DD_APPLICATION_ID) {
      return;
    }
    if (isDashboardPathname(pathname)) {
      datadogRum.startSessionReplayRecording();
    } else {
      datadogRum.stopSessionReplayRecording();
    }
  }, [pathname]);

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_DD_APPLICATION_ID) {
      return;
    }
    datadogRum.setViewName(routeTemplate);
    datadogRum.setViewContextProperty("locale", locale);
  }, [path, routeTemplate, locale]);

  return <>{children}</>;
};
